import Slide from "@mui/material/Slide";
import { useDeepCompareMemo } from "@react-hookz/web";
import { useScroll, useThrottleEffect } from "ahooks";
import { useSetAtom } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import type { Props as AdSlotProps } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { DynamicAdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot/dynamics";
import { isShowTakeoverAdSlotAtom } from "scmp-app/components/advertisement/takeover-ad-slot/atoms";
import { useTakeoverBottomAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot/hooks";
import type { TypeMagazinesStyleVariant } from "scmp-app/components/section/section-style/const";

import CloseIcon from "./icon-close.svg";
import { AdSlotContainer, CloseButton, Container } from "./styles";

type Props = {
  adSlotProps?: Pick<AdSlotProps, "targeting" | "zone">;
  className?: string;
  variant?: "home" | TypeMagazinesStyleVariant;
};

export const TakeoverAdSlot: FunctionComponent<Props> = ({ adSlotProps, className, variant }) => {
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  const { isEnableTakeoverBottomAdSlot, takeoverAdSlotValue } = useTakeoverBottomAdSlot({
    adSlotProps,
    variant,
  });
  const [isClose, setIsClose] = useState(false);

  const [isAdVisible, setIsAdVisible] = useState(false);
  const scrollPosition = useScroll();
  const isShow = isEnableTakeoverBottomAdSlot && isAdVisible;
  const setIsShowTakeoverAdSlot = useSetAtom(isShowTakeoverAdSlotAtom);

  useEffect(() => {
    setIsShowTakeoverAdSlot(isShow);

    return () => {
      setIsShowTakeoverAdSlot(false);
    };
  }, [isShow, setIsShowTakeoverAdSlot]);

  useThrottleEffect(
    () => {
      if (scrollPosition && scrollPosition?.top > (isDesktopUp ? 300 : 200)) {
        const { clientHeight, scrollHeight } = document.documentElement;
        const isAtBottom = Math.round(scrollPosition.top) + clientHeight >= scrollHeight;
        if (isAtBottom) {
          setIsAdVisible(false);
          return;
        }
        setIsAdVisible(true);
        return;
      }
      setIsAdVisible(false);
    },
    [scrollPosition?.top],
    { wait: 200 },
  );

  const responsiveAdSlot = useDeepCompareMemo(() => {
    if (!takeoverAdSlotValue) return null;

    return isDesktopUp ? (
      <DynamicAdSlot
        {...takeoverAdSlotValue}
        sizes={[
          [970, 90],
          [728, 90],
        ]}
      />
    ) : (
      <DynamicAdSlot
        {...takeoverAdSlotValue}
        sizes={[
          [300, 50],
          [320, 50],
        ]}
      />
    );
  }, [takeoverAdSlotValue, isDesktopUp]);

  if (!isEnableTakeoverBottomAdSlot || isClose) return null;

  return (
    <Slide className={className} direction="up" in={isAdVisible} timeout={300}>
      <Container>
        <CloseButton onClick={() => setIsClose(true)}>
          <CloseIcon />
        </CloseButton>
        <AdSlotContainer $height={isDesktopUp ? 90 : 50}>{responsiveAdSlot}</AdSlotContainer>
      </Container>
    </Slide>
  );
};

TakeoverAdSlot.displayName = "TakeoverAdSlot";
